import React, { useState } from "react";

import { useTranslation } from "react-i18next";
import { isEqual } from "lodash";

import AddToLibraryButton from "../../../../../../components/ui/add-to-library-button/AddToLibraryButton";
import IconPicker from "../../../../../../components/ui/icon-picker/IconPicker";
import {
  ButtonGroup,
  TextInput as Text,
  TextAreaInput as TextArea,
} from "@caelor/ui";
import { PANEL_TYPES } from "../../../../../../features/panel-simple/panel-types";
import { useOpticsStore } from "../../../../../../stores/use-optics-store";
import { usePanelSimpleStore } from "../../../../../../stores/use-panel-simple-store";
import {
  OptionContainer,
  OptionContent,
  OptionLabel,
  SettingsOptionContainer,
  SettingsOptionHeading,
  TextEditorContainer,
} from "../../styles";
import { LIBRARY_SERVICE_ACTION, PANEL_PROP } from "./defaults";
import { BOOLEAN_OPTIONS, CORNERS_OPTIONS } from "./options";
import { panelLibraryService } from "@Shared/Service/PanelLibraryService";
import ColorPicker from "../../../../../../components/ui/color-picker/ColorPicker";
import { TextEditor } from "@caelor/cards-and-panels-components";
import { useTheme } from "styled-components";

const PanelsSettings = () => {
  const theme = useTheme();
  const [isRequestingToLibrary, setIsRequestingToLibrary] = useState(false);

  const { data, setData } = usePanelSimpleStore((state) => ({
    data: state.data,
    setData: state.setData,
  }));

  const { libraryPanels, setLibraryPanels, hasStyleguidePermission } =
    useOpticsStore((state) => ({
      libraryPanels: state.libraryPanels,
      setLibraryPanels: state.setLibraryPanels,
      hasStyleguidePermission: state.hasStyleguidePermission,
    }));

  const selectedLibraryPanel = libraryPanels.find(
    (panel) => panel.id === data.id
  );

  const hasIcon = data[PANEL_PROP.DISPLAY_ELEMENTS].includes("icon");
  const hasText = data[PANEL_PROP.DISPLAY_ELEMENTS].includes("text");
  const hasTitle = data[PANEL_PROP.DISPLAY_ELEMENTS].includes("title");

  const { t } = useTranslation();

  const handlePanelLibraryRequest = async (fnName) => {
    try {
      setIsRequestingToLibrary(true);

      const response = await panelLibraryService[fnName](data);
      if (response?.data?.panelId) {
        setData({ id: response.data.panelId });
        setLibraryPanels([
          ...libraryPanels,
          { ...data, id: response.data.panelId },
        ]);
      } else {
        setLibraryPanels([
          ...libraryPanels.map((panel) =>
            panel.id === data.id ? { ...data } : panel
          ),
        ]);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsRequestingToLibrary(false);
    }
  };

  const toggleElement = (data, value, element) => {
    return {
      [PANEL_PROP.DISPLAY_ELEMENTS]: value
        ? [...data[PANEL_PROP.DISPLAY_ELEMENTS], element]
        : data[PANEL_PROP.DISPLAY_ELEMENTS].filter((item) => item !== element),
    };
  };

  return (
    <>
      <SettingsOptionContainer>
        <SettingsOptionHeading>
          {t("flexible-display-items")}
        </SettingsOptionHeading>
        {data[PANEL_PROP.TYPE] !== PANEL_TYPES.DOT && (
          <OptionContainer>
            <OptionLabel>{t("icon")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={BOOLEAN_OPTIONS}
                value={hasIcon}
                onClick={(isOn) => setData(toggleElement(data, isOn, "icon"))}
                i18n={t}
              />
            </OptionContent>
          </OptionContainer>
        )}
        <OptionContainer>
          <OptionLabel>{t("title")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={BOOLEAN_OPTIONS}
              value={hasTitle}
              onClick={(isOn) => {
                let value = toggleElement(data, isOn, "title");

                if (!isOn && !hasText) {
                  value = toggleElement(value, true, "text");
                }

                setData(value);
              }}
              i18n={t}
            />
          </OptionContent>
        </OptionContainer>
        <OptionContainer>
          <OptionLabel>{t("subtext")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={BOOLEAN_OPTIONS}
              value={hasText}
              onClick={(isOn) => {
                let value = toggleElement(data, isOn, "text");

                if (!isOn && !hasTitle) {
                  value = toggleElement(value, true, "title");
                }

                setData(value);
              }}
              i18n={t}
            />
          </OptionContent>
        </OptionContainer>
      </SettingsOptionContainer>
      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("content")}</SettingsOptionHeading>
        {hasIcon && data[PANEL_PROP.TYPE] !== PANEL_TYPES.STANDARD && (
          <OptionContainer>
            <OptionLabel>{t("icon")}</OptionLabel>
            <OptionContent>
              <IconPicker
                tabs={
                  data[PANEL_PROP.TYPE] === PANEL_TYPES.CUSTOM_EMOJI
                    ? ["Emojis", "Logos"]
                    : ["Icons", "Logos"]
                }
                placeholder={t("add")}
                icon={data[PANEL_PROP.ICON]}
                onChange={({ value }) => setData({ [PANEL_PROP.ICON]: value })}
                defaultTab={
                  data[PANEL_PROP.TYPE] === PANEL_TYPES.CUSTOM_EMOJI
                    ? "Emojis"
                    : "Icons"
                }
              />
            </OptionContent>
          </OptionContainer>
        )}
        {hasTitle && (
          <OptionContainer>
            <OptionLabel>{t("title")}</OptionLabel>
            <OptionContent>
              <Text
                placeholder={t("add")}
                value={data[PANEL_PROP.TITLE]}
                onChange={(value) => setData({ [PANEL_PROP.TITLE]: value })}
              />
            </OptionContent>
          </OptionContainer>
        )}
        {hasText && (
          <OptionContainer direction="column">
            <OptionLabel>{t("subtext")}</OptionLabel>
            <TextEditorContainer>
              <TextEditor
                textColor={theme.text.input}
                linkBackgroundColor={theme.bg.primary}
                linkButtonsColor={theme.bg.primary}
                linkIconColor={theme.border.black50}
                linkActionBackgroundColor={theme.bg.primary}
                value={data[PANEL_PROP.TEXT]}
                onChangeComplete={(value) =>
                  setData({ [PANEL_PROP.TEXT]: value })
                }
              />
            </TextEditorContainer>
          </OptionContainer>
        )}
      </SettingsOptionContainer>

      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("styling-options")}</SettingsOptionHeading>
        <OptionContainer>
          <OptionLabel>{t("corners")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={CORNERS_OPTIONS}
              value={data[PANEL_PROP.BORDER_RADIUS]}
              options={{ input: true }}
              onClick={(value) =>
                setData({ [PANEL_PROP.BORDER_RADIUS]: value })
              }
              onChange={({ target: { value } }) => {
                if (value.length < 4) {
                  setData({ [PANEL_PROP.BORDER_RADIUS]: value });
                }
              }}
              i18n={t}
            />
          </OptionContent>
        </OptionContainer>
        {data[PANEL_PROP.TYPE] !== PANEL_TYPES.STANDARD && (
          <OptionContainer>
            <OptionLabel>{t("colors")}</OptionLabel>
            <OptionContent>
              <ColorPicker
                value={{
                  accent: data[PANEL_PROP.ACCENT_COLOR],
                  text: data[PANEL_PROP.TEXT_COLOR],
                }}
                onChange={(color) =>
                  setData({
                    [PANEL_PROP.ACCENT_COLOR]: color.accent,
                    [PANEL_PROP.TEXT_COLOR]: color.text,
                  })
                }
                hideColorTiles={data[PANEL_PROP.COLOR_MUTE]}
              />
            </OptionContent>
          </OptionContainer>
        )}
        {data[PANEL_PROP.TYPE] !== PANEL_TYPES.STANDARD && (
          <OptionContainer>
            <OptionLabel>{t("color-mute")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={BOOLEAN_OPTIONS}
                value={data[PANEL_PROP.COLOR_MUTE]}
                onClick={() =>
                  setData({
                    [PANEL_PROP.COLOR_MUTE]: !data[PANEL_PROP.COLOR_MUTE],
                  })
                }
                i18n={t}
              />
            </OptionContent>
          </OptionContainer>
        )}
      </SettingsOptionContainer>

      {hasStyleguidePermission && (
        <SettingsOptionContainer style={{ border: "none", marginTop: "auto" }}>
          <OptionContainer>
            <AddToLibraryButton
              onClick={(action) =>
                handlePanelLibraryRequest(LIBRARY_SERVICE_ACTION[action])
              }
              isSaved={!!selectedLibraryPanel}
              isLoading={isRequestingToLibrary}
              hasChanges={
                selectedLibraryPanel
                  ? !isEqual(selectedLibraryPanel, data)
                  : false
              }
              macroName="panel"
            />
          </OptionContainer>
        </SettingsOptionContainer>
      )}
    </>
  );
};

export default PanelsSettings;
